import React from "react";
import { Link } from "react-router-dom";
import { Info, Truck, FileDown } from "lucide-react";
import { DD_MM_YYYY } from "src/helpers";
import { formatFloatWithTwoDecimals } from "src/utils";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { ActiveAntsPurchaseOrderColumnHeader } from "src/enums";
import { ActiveAntsPurchaseOrder, TableColumn } from "src/types";
import { StatusPill } from "src/components/ui";
import { Table } from "src/components";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/shadcn/ui/tooltip";

interface ActiveAntsPurchaseOrdersDashTableProps {
  activeAntsPurchaseOrders: ActiveAntsPurchaseOrder[];
  columnsToHide?: ActiveAntsPurchaseOrderColumnHeader[];
  onDownloadPDF?: (activeAntsPurchaseOrderId: string) => void;
  isDownloadingPDF?: boolean;
}

const ActiveAntsPurchaseOrdersDashTable: React.FC<ActiveAntsPurchaseOrdersDashTableProps> = ({
  activeAntsPurchaseOrders,
  columnsToHide,
  onDownloadPDF,
  isDownloadingPDF
}) => {
  const activeAntsPurchaseOrdersTableColumns: TableColumn<
    ActiveAntsPurchaseOrderColumnHeader,
    ActiveAntsPurchaseOrder
  >[] = [
    {
      header: ActiveAntsPurchaseOrderColumnHeader.ACTIVE_ANTS_PURCHASE_ORDER,
      column: (row: ActiveAntsPurchaseOrder) => (
        <Link
          to={`${ROUTES_CONFIG.activeAntsPurchaseOrder.path}?id=${row.id}`}
          className="text-primary-900 hover:text-primary-700 hover:underline transition-colors whitespace-nowrap"
        >
          {row.id}
        </Link>
      ),
      sticky: true
    },
    {
      header: ActiveAntsPurchaseOrderColumnHeader.STATUS,
      column: (row: ActiveAntsPurchaseOrder) => (
        <span className="flex justify-center">{row.status && <StatusPill status={row.status} />}</span>
      ),
      styles: { textAlign: "center", minWidth: "140px" }
    },
    {
      header: ActiveAntsPurchaseOrderColumnHeader.CHECK_IN_STATUS,
      column: (row: ActiveAntsPurchaseOrder) => {
        const hasDiscrepancyToShow = (row.checkInFlaggedIssues?.length ?? 0) > 0 || row.checkInStatus === "Resolved";
        return (
          <span className="flex justify-center">
            {row.checkInStatus && (
              <StatusPill
                status={row.checkInStatus}
                linkPath={
                  hasDiscrepancyToShow
                    ? `${ROUTES_CONFIG.activeAntsReceivingDiscrepancies.path}?id=${row.id}`
                    : undefined
                }
              />
            )}
          </span>
        );
      },
      styles: { textAlign: "center", minWidth: "140px" }
    },
    {
      header: ActiveAntsPurchaseOrderColumnHeader.SHIPMENT,
      column: (row: ActiveAntsPurchaseOrder) =>
        row.shipmentId ? (
          <Link
            to={`${ROUTES_CONFIG.viewShipment.path}?id=${row.shipmentId}`}
            className="inline-flex items-center gap-1 text-primary-900 hover:text-primary-700 hover:underline transition-colors whitespace-nowrap"
          >
            <Truck className="h-4 w-4" />
            <span>{row.shipmentId}</span>
          </Link>
        ) : (
          <span>-</span>
        ),
      styles: { minWidth: "200px" }
    },
    {
      header: ActiveAntsPurchaseOrderColumnHeader.PRODUCTS_COUNT,
      column: (row: ActiveAntsPurchaseOrder) => <span className="text-center block">{row.products?.length ?? 0}</span>,
      styles: { textAlign: "center", minWidth: "100px" }
    },
    {
      header: ActiveAntsPurchaseOrderColumnHeader.COST,
      column: (row: ActiveAntsPurchaseOrder) => (row.totalCost ? formatFloatWithTwoDecimals(row.totalCost) : undefined),
      roleRequired: ["HEALF_OPERATIONS_TEAM", "HEALF_ACCOUNTANT"],
      styles: { textAlign: "center" }
    },
    {
      header: ActiveAntsPurchaseOrderColumnHeader.HMRC_COST,
      column: (row: ActiveAntsPurchaseOrder) =>
        row.hmrcTotalCost ? formatFloatWithTwoDecimals(row.hmrcTotalCost) : undefined,
      roleRequired: ["HEALF_OPERATIONS_TEAM", "HEALF_ACCOUNTANT"],
      styles: { textAlign: "center" }
    },
    {
      header: ActiveAntsPurchaseOrderColumnHeader.CREATION_DATE,
      column: (row: ActiveAntsPurchaseOrder) => (
        <span className="whitespace-nowrap">{row.createdAt ? DD_MM_YYYY.formatDate(row.createdAt) : "-"}</span>
      ),
      styles: { minWidth: "120px" }
    },
    {
      header: ActiveAntsPurchaseOrderColumnHeader.ACTIONS,
      column: (row: ActiveAntsPurchaseOrder) => (
        <TooltipProvider>
          <div className="flex items-center justify-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={`${ROUTES_CONFIG.activeAntsPurchaseOrder.path}?id=${row.id}`}
                  className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                >
                  <Info className="h-4 w-4 text-muted-600" />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>More Info</p>
              </TooltipContent>
            </Tooltip>
            {row.shipmentId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={`${ROUTES_CONFIG.viewShipment.path}?id=${row.shipmentId}`}
                    className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                  >
                    <Truck className="h-4 w-4 text-muted-600" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View Shipment</p>
                </TooltipContent>
              </Tooltip>
            )}
            {row.pdfS3Key && onDownloadPDF && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => onDownloadPDF(row.id)}
                    disabled={isDownloadingPDF}
                    className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FileDown className="h-4 w-4 text-muted-600" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Download PDF</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>
      ),
      styles: { textAlign: "center" }
    }
  ];

  return (
    <Table<ActiveAntsPurchaseOrderColumnHeader, ActiveAntsPurchaseOrder>
      data={activeAntsPurchaseOrders}
      columns={activeAntsPurchaseOrdersTableColumns}
      columnsToHide={columnsToHide}
    />
  );
};

export { ActiveAntsPurchaseOrdersDashTable };
